import { useState, useEffect, useRef } from 'react'
import { motion, useScroll, useTransform, useSpring, useInView, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
    ArrowRight, Zap, Code, Sparkles, Brain, Shield,
    Globe, MessageSquare, CheckCircle, Star, Play,
    Copy, Check, ChevronDown, Menu, X, Users,
    BarChart3, Lock, Rocket, Clock, DollarSign,
    Smartphone, Monitor, Database, Headphones, Award, Settings,
    Layers, Server, Cpu, Cloud
} from 'lucide-react'
import LandingHeader from '../../components/landing/LandingHeader'
import LandingFooter from '../../components/landing/LandingFooter'
import talkidaLogo from '../../assets/icons/logo.png'
import { getSubscriptionPlans } from '../../services/subscription_plan'

const LandingPage = () => {
    const navigate = useNavigate()
    const [copiedCode, setCopiedCode] = useState(false)
    const { scrollYProgress } = useScroll()
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    })

    // Code snippet for embed
    const embedCode = `<!-- Talkida Chat Widget - Plug & Play -->
<script 
  src="https://cdn.talkida.co.in/talkida-widget.js" 
  bot-id="your-bot-id"
  bot-token="your-bot-token"
  data-position="bottom-right"
  data-theme="dark"
  data-primary-color="#3b82f6"
  defer>
</script>

<!-- That's it! Your AI chatbot is live 🚀 -->`

    const handleCopyCode = () => {
        navigator.clipboard.writeText(embedCode)
        setCopiedCode(true)
        setTimeout(() => setCopiedCode(false), 2000)
    }

    const handleGetStarted = () => {
        navigate('/signin')
    }

    return (
        <>
            <Helmet>
                {/* Primary Meta Tags */}
                <title>Talkida Technologies - Complete IT Solutions | Mobile Apps, Web Development & AI</title>
                <meta name="title" content="Talkida Technologies - Complete IT Solutions | Mobile Apps, Web Development & AI" />
                <meta name="description" content="End-to-end IT solutions company specializing in mobile app development, web applications, AI systems, and enterprise software. Professional development team with 7-day free support. From startups to enterprises." />
                <meta name="keywords" content="IT solutions, mobile app development, web development, AI solutions, software development, enterprise software, custom applications, Talkida Technologies, IT company India, software company, digital transformation, business automation" />
                <meta name="author" content="Talkida Technologies" />
                <meta name="robots" content="index, follow" />
                <meta name="language" content="English" />
                <meta name="revisit-after" content="7 days" />
                <link rel="canonical" href="https://talkida.co.in/" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://talkida.co.in/" />
                <meta property="og:title" content="Talkida Technologies - Complete IT Solutions | Mobile Apps, Web Development & AI" />
                <meta property="og:description" content="End-to-end IT solutions company specializing in mobile app development, web applications, AI systems, and enterprise software. Professional development team with 7-day free support." />
                <meta property="og:image" content="https://talkida.co.in/favicon.png" />
                <meta property="og:site_name" content="Talkida" />
                <meta property="og:locale" content="en_US" />

                {/* Twitter */}
                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:url" content="https://talkida.co.in/" />
                <meta property="twitter:title" content="Talkida Technologies - Complete IT Solutions | Mobile Apps, Web Development & AI" />
                <meta property="twitter:description" content="End-to-end IT solutions company specializing in mobile app development, web applications, AI systems, and enterprise software. Professional development team with 7-day free support." />
                <meta property="twitter:image" content="https://talkida.co.in/favicon.png" />

                {/* Additional SEO Meta Tags */}
                <meta name="geo.region" content="IN" />
                <meta name="geo.placename" content="India" />
                <meta name="theme-color" content="#3b82f6" />
                <meta name="apple-mobile-web-app-capable" content="yes" />
                <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />

                {/* Structured Data - JSON-LD */}
                <script type="application/ld+json" dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "Talkida",
                        "applicationCategory": "BusinessApplication",
                        "operatingSystem": "Web",
                        "offers": {
                            "@type": "Offer",
                            "price": "999",
                            "priceCurrency": "INR",
                            "availability": "https://schema.org/InStock"
                        },
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": "4.8",
                            "ratingCount": "150"
                        },
                        "description": "AI-powered chatbot platform with plug-and-play integration. Setup in 5 Minutes.",
                        "url": "https://talkida.co.in",
                        "author": {
                            "@type": "Organization",
                            "name": "Talkida Technologies",
                            "url": "https://talkida.co.in"
                        }
                    })
                }} />

                {/* Organization Schema */}
                <script type="application/ld+json" dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "Talkida Technologies",
                        "url": "https://talkida.co.in",
                        "logo": "https://talkida.co.in/favicon.png",
                        "contactPoint": {
                            "@type": "ContactPoint",
                            "email": "info@talkida.co.in",
                            "contactType": "customer support"
                        },
                        "sameAs": [
                            "https://www.linkedin.com/company/talkida-ai",
                            "https://github.com/talkida/",
                            "https://www.instagram.com/talkida.co.in/"
                        ]
                    })
                }} />
            </Helmet>
            <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
                
                <div className="relative z-10">
                {/* Progress bar */}
                <motion.div
                    className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 origin-left z-50 shadow-lg"
                    style={{ scaleX }}
                />

                <LandingHeader onGetStarted={handleGetStarted} />

                {/* Hero Section */}
                <HeroSection onGetStarted={handleGetStarted} />

                {/* Floating 3D Elements Background */}
                <FloatingElements />

                {/* Plug & Play Section */}
                <PlugNPlaySection
                    embedCode={embedCode}
                    copiedCode={copiedCode}
                    handleCopyCode={handleCopyCode}
                />

                {/* Features Section */}
                <FeaturesSection />

                {/* How It Works */}
                <HowItWorksSection />

                {/* Stats Section */}
                <StatsSection />

                {/* Our Clients */}
                <OurClientsSection />

                {/* Our Services */}
                <OurServicesSection />

                {/* Pricing Preview */}
                <PricingSection onGetStarted={handleGetStarted} navigate={navigate} />

                {/* CTA Section */}
                <CTASection onGetStarted={handleGetStarted} />

                <LandingFooter />
                </div>
            </div>
        </>
    )
}

// Hero Section Component
const HeroSection = ({ onGetStarted }) => {
    const heroRef = useRef(null)
    const isInView = useInView(heroRef, { once: true, amount: 0.3 })

    // Animated changing text
    const changingTexts = [
        'Modern Businesses',
        'Digital Transformation',
        'Smart Automation',
        'Scalable Growth'
    ]
    const [currentTextIndex, setCurrentTextIndex] = useState(0)

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTextIndex((prevIndex) => (prevIndex + 1) % changingTexts.length)
        }, 3000) // Change text every 3 seconds

        return () => clearInterval(interval)
    }, [changingTexts.length])

    return (
        <section ref={heroRef} className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
            {/* Animated gradient orbs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <motion.div
                    className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.5, 0.3],
                    }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl"
                    animate={{
                        scale: [1.2, 1, 1.2],
                        opacity: [0.5, 0.3, 0.5],
                    }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                />
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 50 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                >
                    {/* Badge */}
                    <motion.div
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={isInView ? { opacity: 1, scale: 1 } : {}}
                        transition={{ delay: 0.2 }}
                    >
                        <Sparkles className="w-4 h-4 text-yellow-400" />
                        <span className="text-sm font-medium text-white">End-to-End IT Solutions Company</span>
                    </motion.div>

                    {/* Main Heading */}
                    <motion.h1
                        className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.3, duration: 0.8 }}
                    >
                        Complete IT Solutions for
                        <br />
                        <span className="relative inline-block min-h-[1.2em]">
                            <AnimatePresence mode="wait">
                                <motion.span
                                    key={currentTextIndex}
                                    className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 inline-block"
                                    initial={{ opacity: 0, y: 20, scale: 0.9 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -20, scale: 0.9 }}
                                    transition={{ duration: 0.5, ease: "easeInOut" }}
                                >
                                    {changingTexts[currentTextIndex]}
                                </motion.span>
                            </AnimatePresence>
                        </span>
                    </motion.h1>

                    {/* Subheading */}
                    <motion.p
                        className="text-xl sm:text-2xl text-white/80 mb-12 max-w-3xl mx-auto"
                        initial={{ opacity: 0 }}
                        animate={isInView ? { opacity: 1 } : {}}
                        transition={{ delay: 0.5 }}
                    >
                        From <span className="font-bold text-white">Mobile Apps</span> to <span className="font-bold text-white">AI Solutions</span> - we deliver end-to-end software development.
                        Professional development team with enterprise-grade solutions and 7-day free support.
                    </motion.p>

                    {/* CTA Buttons */}
                    <motion.div
                        className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.7 }}
                    >
                        <motion.button
                            onClick={onGetStarted}
                            className="group relative px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-full shadow-lg hover:shadow-purple-500/50 transition-all duration-300 flex items-center gap-2"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Get Quote Today
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </motion.button>

                        <motion.button
                            onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
                            className="px-8 py-4 bg-white/10 backdrop-blur-md text-white font-semibold rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300 flex items-center gap-2"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Play className="w-5 h-5" />
                            See It In Action
                        </motion.button>
                    </motion.div>

                    {/* Trust badges */}
                    <motion.div
                        className="mt-16 flex flex-wrap justify-center items-center gap-8 text-white/60"
                        initial={{ opacity: 0 }}
                        animate={isInView ? { opacity: 1 } : {}}
                        transition={{ delay: 0.9 }}
                    >
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            <span>Free Consultation</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            <span>7-Day Free Support</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            <span>Enterprise Grade Quality</span>
                        </div>
                    </motion.div>
                </motion.div>
            </div>

            {/* Scroll indicator */}
            <motion.div
                className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
            >
                <ChevronDown className="w-8 h-8 text-white/40" />
            </motion.div>
        </section>
    )
}

// Plug & Play Section
const PlugNPlaySection = ({ embedCode, copiedCode, handleCopyCode }) => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: true, amount: 0.3 })

    return (
        <section id="demo" ref={sectionRef} className="relative py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
                <motion.div
                    className="text-center mb-12"
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                >
                    <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                        Truly <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">Plug-n-Play</span>
                    </h2>
                    <p className="text-xl text-white/70 max-w-2xl mx-auto">
                        No complex setup. No technical knowledge needed. Just copy, paste, and go live.
                    </p>
                </motion.div>

                {/* Code Editor Mockup */}
                <motion.div
                    className="relative"
                    initial={{ opacity: 0, y: 50 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.2 }}
                >
                    <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
                        {/* Editor Header */}
                        <div className="bg-slate-900/80 px-4 py-3 flex items-center justify-between border-b border-white/10">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            </div>
                            <span className="text-white/60 text-sm font-mono">index.html</span>
                            <button
                                onClick={handleCopyCode}
                                className="flex items-center gap-2 px-3 py-1 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white text-sm"
                            >
                                {copiedCode ? (
                                    <>
                                        <Check className="w-4 h-4 text-green-400" />
                                        <span>Copied!</span>
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4" />
                                        <span>Copy</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Code Content */}
                        <div className="p-6 font-mono text-sm overflow-x-auto">
                            <pre className="text-white/90">
                                <code>
                                    <span className="text-gray-500">{'<!-- Talkida Chat Widget - Plug & Play -->'}</span>
                                    {'\n'}
                                    <span className="text-pink-400">{'<script'}</span>
                                    {'\n  '}
                                    <span className="text-purple-400">src</span>
                                    <span className="text-white">=</span>
                                    <span className="text-green-400">"https://cdn.talkida.co.in/talkida-widget.js"</span>
                                    {'\n  '}
                                    <span className="text-purple-400">bot-id</span>
                                    <span className="text-white">=</span>
                                    <span className="text-green-400">"your-bot-id"</span>
                                    {'\n  '}
                                    <span className="text-purple-400">bot-token</span>
                                    <span className="text-white">=</span>
                                    <span className="text-green-400">"your-bot-token"</span>
                                    {'\n  '}
                                    <span className="text-purple-400">data-position</span>
                                    <span className="text-white">=</span>
                                    <span className="text-green-400">"bottom-right"</span>
                                    {'\n  '}
                                    <span className="text-purple-400">data-theme</span>
                                    <span className="text-white">=</span>
                                    <span className="text-green-400">"dark"</span>
                                    {'\n  '}
                                    <span className="text-purple-400">data-primary-color</span>
                                    <span className="text-white">=</span>
                                    <span className="text-green-400">"#3b82f6"</span>
                                    {'\n  '}
                                    <span className="text-purple-400">defer</span>
                                    <span className="text-pink-400">{'>'}</span>
                                    {'\n'}
                                    <span className="text-pink-400">{'</script>'}</span>
                                    {'\n\n'}
                                    <span className="text-gray-500">{"<!-- That's it! Your AI chatbot is live 🚀 -->"}</span>
                                </code>
                            </pre>
                        </div>
                    </div>

                    {/* 3D floating elements around code */}
                    <motion.div
                        className="absolute -top-8 -right-8 w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl opacity-20 blur-xl"
                        animate={{
                            rotate: [0, 360],
                            scale: [1, 1.2, 1],
                        }}
                        transition={{ duration: 10, repeat: Infinity }}
                    />
                    <motion.div
                        className="absolute -bottom-8 -left-8 w-32 h-32 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full opacity-20 blur-xl"
                        animate={{
                            rotate: [360, 0],
                            scale: [1.2, 1, 1.2],
                        }}
                        transition={{ duration: 8, repeat: Infinity }}
                    />
                </motion.div>

                {/* Steps */}
                <motion.div
                    className="grid md:grid-cols-3 gap-8 mt-16"
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : {}}
                    transition={{ delay: 0.4 }}
                >
                    {[
                        { step: '1', title: 'Sign Up', desc: 'Create your account in 30 seconds', icon: Users },
                        { step: '2', title: 'Configure', desc: 'Customize your AI bot with our visual editor', icon: Brain },
                        { step: '3', title: 'Deploy', desc: 'Copy-paste one line of code. Go live in 5 Minutes!', icon: Rocket }
                    ].map((item, index) => (
                        <motion.div
                            key={item.step}
                            className="relative group"
                            initial={{ opacity: 0, y: 20 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.5 + index * 0.1 }}
                        >
                            <div className="bg-white/5 backdrop-blur-md p-6 rounded-2xl border border-white/10 hover:border-purple-500/50 transition-all duration-300 hover:bg-white/10 h-[180px] flex flex-col">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                                        {item.step}
                                    </div>
                                    <item.icon className="w-8 h-8 text-purple-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                                <p className="text-white/60 flex-grow">{item.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    )
}

// Features Section
const FeaturesSection = () => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: true, amount: 0.2 })

    const features = [
        {
            icon: Zap,
            title: 'Lightning Fast Setup',
            description: 'Get your AI chatbot live in under 30 seconds. No technical skills required.',
            gradient: 'from-yellow-400 to-orange-500'
        },
        {
            icon: Brain,
            title: 'Powered by Advanced AI',
            description: 'Leverage GPT-4, Claude, Gemini, and other cutting-edge AI models.',
            gradient: 'from-purple-400 to-pink-500'
        },
        {
            icon: Code,
            title: 'Developer Friendly',
            description: 'Simple API, extensive docs, and SDKs for all major platforms.',
            gradient: 'from-blue-400 to-cyan-500'
        },
        {
            icon: Globe,
            title: 'Multi-Language Support',
            description: 'Communicate with customers in 100+ languages automatically.',
            gradient: 'from-green-400 to-emerald-500'
        },
        {
            icon: Shield,
            title: 'Enterprise Security',
            description: 'Bank-level encryption, GDPR compliant, SOC 2 certified.',
            gradient: 'from-red-400 to-pink-500'
        },
        {
            icon: BarChart3,
            title: 'Real-Time Analytics',
            description: 'Track conversations, user satisfaction, and bot performance.',
            gradient: 'from-indigo-400 to-purple-500'
        }
    ]

    return (
        <section id="features" ref={sectionRef} className="relative py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                >
                    <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                        Built with Excellence <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">in Mind</span>
                    </h2>
                    <p className="text-xl text-white/70 max-w-2xl mx-auto">
                        Every Talkida AI product is engineered with enterprise-grade quality, security, and performance that you can trust without doubt
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {features.map((feature, index) => (
                        <FeatureCard key={index} feature={feature} index={index} isInView={isInView} />
                    ))}
                </div>
            </div>
        </section>
    )
}

// Feature Card with 3D effect
const FeatureCard = ({ feature, index, isInView }) => {
    const [rotateX, setRotateX] = useState(0)
    const [rotateY, setRotateY] = useState(0)
    const cardRef = useRef(null)

    const handleMouseMove = (e) => {
        if (!cardRef.current) return
        const rect = cardRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        const centerX = rect.width / 2
        const centerY = rect.height / 2
        const rotateXValue = ((y - centerY) / centerY) * -10
        const rotateYValue = ((x - centerX) / centerX) * 10

        setRotateX(rotateXValue)
        setRotateY(rotateYValue)
    }

    const handleMouseLeave = () => {
        setRotateX(0)
        setRotateY(0)
    }

    return (
        <motion.div
            ref={cardRef}
            className="relative group perspective-1000"
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{
                transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
                transformStyle: 'preserve-3d',
                transition: 'transform 0.1s ease-out'
            }}
        >
            <div className="bg-white/5 backdrop-blur-md p-8 rounded-2xl border border-white/10 hover:border-purple-500/50 transition-all duration-300 h-full relative overflow-hidden">
                {/* Gradient background on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />

                <div className="relative z-10">
                    <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                        <feature.icon className="w-7 h-7 text-white" />
                    </div>

                    <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                    <p className="text-white/60 leading-relaxed">{feature.description}</p>
                </div>
            </div>
        </motion.div>
    )
}
// How It Works Section
const HowItWorksSection = () => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: true, amount: 0.3 })

    const steps = [
        {
            title: 'Create Your Bot',
            description: 'Use our intuitive visual editor to configure your AI assistant. Choose from multiple AI models, customize appearance, and set personality.',
            image: '🤖',
            details: ['No coding required', 'Visual customization', 'Choose AI model']
        },
        {
            title: 'Train with Your Data',
            description: 'Upload multiple file types including PDFs, Excel, Word, text files, or connect your knowledge base. Your bot learns everything about your business instantly.',
            image: '📚',
            details: ['Upload PDFs, Excel, Word & text files', 'Auto-train from website', 'API integration']
        },
        {
            title: 'Embed & Launch',
            description: 'Copy one line of code and paste it into your website. Your AI chatbot goes live immediately.',
            image: '🚀',
            details: ['One-line integration', 'Works on any platform', 'Live in 30 seconds']
        }
    ]

    return (
        <section id="how-it-works" ref={sectionRef} className="relative py-20 px-4 sm:px-6 lg:px-8 bg-white/5">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                >
                    <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                        How to Get <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">Started</span> with Talkida AI
                    </h2>
                    <p className="text-xl text-white/70 max-w-2xl mx-auto">
                        Every Talkida AI solution follows the same proven 3-step process - whether it's our AI Assistant, Help & Support, or Voice Intelligence 🚀
                    </p>
                </motion.div>

                <div className="grid lg:grid-cols-3 gap-12">
                    {steps.map((step, index) => (
                        <motion.div
                            key={index}
                            className="relative"
                            initial={{ opacity: 0, x: -50 }}
                            animate={isInView ? { opacity: 1, x: 0 } : {}}
                            transition={{ delay: index * 0.2 }}
                        >
                            {/* Connector line */}
                            {index < steps.length - 1 && (
                                <div className="hidden lg:block absolute top-24 left-full w-full h-0.5 bg-gradient-to-r from-purple-500/50 to-transparent" />
                            )}

                            <div className="relative pt-4">
                                {/* Step number */}
                                <div className="absolute -top-2 -left-4 w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg z-10">
                                    {index + 1}
                                </div>

                                <div className="bg-white/5 backdrop-blur-md p-8 rounded-2xl border border-white/10 hover:border-purple-500/50 transition-all duration-300 relative h-[400px] flex flex-col">
                                    <div className="text-6xl mb-6">{step.image}</div>
                                    <h3 className="text-2xl font-semibold text-white mb-4">{step.title}</h3>
                                    <p className="text-white/70 mb-6 flex-grow">{step.description}</p>

                                    <ul className="space-y-2 mt-auto">
                                        {step.details.map((detail, i) => (
                                            <li key={i} className="flex items-center gap-2 text-white/60 text-sm">
                                                <CheckCircle className="w-4 h-4 text-green-400" />
                                                {detail}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    )
}

// Stats Section
const StatsSection = () => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: true })

    const stats = [
        { value: '5 min', label: 'Average Setup Time', icon: Clock },
        { value: '99.9%', label: 'Uptime Guarantee', icon: Shield },
        { value: '10+', label: 'Languages Supported', icon: Globe },
        { value: '24/7', label: 'Customer Support', icon: MessageSquare }
    ]

    return (
        <section ref={sectionRef} className="relative py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {stats.map((stat, index) => (
                        <motion.div
                            key={index}
                            className="text-center"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={isInView ? { opacity: 1, scale: 1 } : {}}
                            transition={{ delay: index * 0.1 }}
                        >
                            <div className="bg-white/5 backdrop-blur-md p-8 rounded-2xl border border-white/10">
                                <stat.icon className="w-12 h-12 text-purple-400 mx-auto mb-4" />
                                <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                                <div className="text-white/60">{stat.label}</div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    )
}

// Our Services Section
const OurServicesSection = () => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: true, amount: 0.2 })

    const services = [
        {
            icon: Smartphone,
            title: 'Mobile App Development',
            description: 'Native Android & iOS apps with Play Store and App Store deployment. From concept to launch.',
            features: ['Android Development', 'iOS Development', 'Play Store Deployment', 'App Store Publishing'],
            gradient: 'from-green-400 to-blue-500'
        },
        {
            icon: Layers,
            title: 'Web Solutions & Dashboards',
            description: 'Custom web applications, admin dashboards, and responsive websites built with modern technologies.',
            features: ['Custom Web Apps', 'Admin Dashboards', 'Responsive Design', 'Modern Tech Stack'],
            gradient: 'from-blue-400 to-purple-500'
        },
        {
            icon: Server,
            title: 'Backend & Database Solutions',
            description: 'Scalable backend systems, API development, and database architecture for enterprise applications.',
            features: ['API Development', 'Database Design', 'Cloud Integration', 'Scalable Architecture'],
            gradient: 'from-purple-400 to-pink-500'
        },
        {
            icon: Cpu,
            title: 'AI-Powered Systems',
            description: 'Next-generation AI solutions including intelligent chatbots, automation systems, and generative AI integrations.',
            features: ['AI Chatbots', 'Generative AI Integration', 'Process Automation', 'Intelligent Analytics'],
            gradient: 'from-orange-400 to-red-500'
        },
        {
            icon: Shield,
            title: 'Enterprise Security',
            description: 'Comprehensive security solutions, compliance management, and data protection for enterprise clients.',
            features: ['Security Audits', 'Compliance Management', 'Data Protection', 'Access Control'],
            gradient: 'from-red-400 to-pink-500'
        },
        {
            icon: Cloud,
            title: 'DevOps & Cloud Solutions',
            description: 'Complete DevOps setup, cloud deployment, and ongoing maintenance with 7-day post-delivery support.',
            features: ['DevOps Setup', 'Cloud Deployment', '7-Day Free Support', 'Ongoing Maintenance'],
            gradient: 'from-indigo-400 to-purple-500'
        }
    ]

    return (
        <section id="services" ref={sectionRef} className="relative py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                >
                    <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                        Your Trusted <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">Technology Partner</span>
                    </h2>
                    <p className="text-xl text-white/70 max-w-3xl mx-auto">
                        From concept to deployment, we deliver end-to-end software solutions. Our senior development team ensures quality delivery with comprehensive post-launch support.
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {services.map((service, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 50 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: index * 0.1, duration: 0.5 }}
                            className="group relative h-[350px]"
                        >
                            <div className="bg-white/5 backdrop-blur-md p-8 rounded-2xl border border-white/10 hover:border-purple-500/50 transition-all duration-300 h-full flex flex-col relative overflow-hidden group">
                                {/* Gradient background on hover */}
                                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />

                                <div className="relative z-10 flex flex-col h-full">
                                    {/* Icon section */}
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className={`w-14 h-14 bg-gradient-to-br ${service.gradient} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                                            <service.icon className="w-7 h-7 text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-xl font-semibold text-white">{service.title}</h3>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <p className="text-white/60 leading-relaxed mb-6 flex-grow">{service.description}</p>

                                    {/* Features */}
                                    <ul className="space-y-2">
                                        {service.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-center gap-2 text-white/70">
                                                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                                                <span className="text-sm">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Quality Assurance Banner */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.8 }}
                    className="mt-16 bg-gradient-to-br from-slate-800/40 via-purple-900/30 to-slate-800/40 backdrop-blur-xl border border-white/10 rounded-3xl p-12 text-center"
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                        {/* Card 1 */}
                        <div className="group relative p-8 h-[280px] flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-blue-500/25 transition-all duration-300 flex-shrink-0">
                                <Award className="w-7 h-7 text-white" style={{ strokeWidth: 1.5 }} />
                            </div>
                            <h4 className="text-xl font-bold text-white mb-4">Excellence Guaranteed</h4>
                            <p className="text-white/70 leading-relaxed flex-grow">Senior developers with 5+ years experience ensuring top-quality deliverables</p>
                        </div>
                        
                        {/* Card 2 */}
                        <div className="group relative p-8 h-[280px] flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-green-500/25 transition-all duration-300 flex-shrink-0">
                                <Headphones className="w-7 h-7 text-white" style={{ strokeWidth: 1.5 }} />
                            </div>
                            <h4 className="text-xl font-bold text-white mb-4">7-Day Free Support</h4>
                            <p className="text-white/70 leading-relaxed flex-grow">Comprehensive post-delivery support to ensure smooth operation and quick issue resolution</p>
                        </div>
                        
                        {/* Card 3 */}
                        <div className="group relative p-8 h-[280px] flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-purple-500/25 transition-all duration-300 flex-shrink-0">
                                <Rocket className="w-7 h-7 text-white" style={{ strokeWidth: 1.5 }} />
                            </div>
                            <h4 className="text-xl font-bold text-white mb-4">Complete Project Delivery</h4>
                            <p className="text-white/70 leading-relaxed flex-grow">From initial concept to final deployment and go-live support with documentation</p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    )
}
// Pricing Section
const PricingSection = ({ onGetStarted, navigate }) => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: true, amount: 0.3 })
    const [plans, setPlans] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)

    // Helper function to convert plan names to proper case
    const formatPlanName = (name) => {
        if (!name) return ''
        return name.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
    }

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                setIsLoading(true)
                setError(null)
                const response = await getSubscriptionPlans()
                console.log('Subscription plans response:', response)

                if (response.error) {
                    console.log('Error in response:', response.msg)
                    setError(response.msg);
                    setPlans([])
                    return
                }

                const sortedPlans = response.data
                    .map(plan => ({
                        ...plan,
                        is_popular: plan.name === 'PROFESSIONAL' // Mark Professional as popular
                    }))
                    .sort((a, b) => {
                        // Sort order: FREE, BASIC, PROFESSIONAL, ENTERPRISE
                        const order = { 'FREE': 1, 'BASIC': 2, 'PROFESSIONAL': 3, 'ENTERPRISE': 4 }
                        return (order[a.name] || 999) - (order[b.name] || 999)
                    })

                console.log('Sorted plans:', sortedPlans)
                setPlans(sortedPlans)
            } catch (err) {
                console.error('Error fetching subscription plans:', err)
                setError(`Failed to load pricing plans: ${err.message}`)
                setPlans([])
            } finally {
                setIsLoading(false)
            }
        }

        fetchPlans()
    }, [])

    return (
        <section id="pricing" ref={sectionRef} className="relative py-20 px-4 sm:px-6 lg:px-8 bg-white/5">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                >
                    <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                        Talkida AI Assistant <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">Pricing</span>
                    </h2>
                    <p className="text-xl text-white/70 max-w-2xl mx-auto mb-6">
                        Start free. Scale as you grow. No hidden fees.
                    </p>
                    <button
                        onClick={() => navigate('/products')}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-md text-white font-medium rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300"
                    >
                        View All Products & Pricing
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </motion.div>

                {isLoading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="text-white/60">Loading pricing plans...</div>
                    </div>
                ) : error ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="text-red-400">{error}</div>
                    </div>
                ) : plans.length === 0 ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="text-white/60">No pricing plans available</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto pb-4 pt-8 mt-8 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 scrollbar-hide" style={{ scrollBehavior: 'smooth' }}>
                        <style>{`
                            .scrollbar-hide::-webkit-scrollbar {
                                height: 8px;
                            }
                            .scrollbar-hide::-webkit-scrollbar-track {
                                background: rgba(255, 255, 255, 0.1);
                                border-radius: 4px;
                            }
                            .scrollbar-hide::-webkit-scrollbar-thumb {
                                background: rgba(147, 51, 234, 0.5);
                                border-radius: 4px;
                            }
                            .scrollbar-hide::-webkit-scrollbar-thumb:hover {
                                background: rgba(147, 51, 234, 0.7);
                            }
                        `}</style>
                        <div className="flex gap-6 min-w-max items-stretch">
                            {plans.map((plan, index) => (
                                <motion.div
                                    key={plan.id}
                                    className="relative flex-shrink-0 w-80 mt-6 flex"
                                    initial={{ opacity: 0, y: 50 }}
                                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    {plan.is_popular && (
                                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-2 rounded-full text-white text-sm font-semibold z-50 whitespace-nowrap shadow-lg">
                                            Most Popular
                                        </div>
                                    )}

                                    <div className={`bg-white/5 backdrop-blur-md p-8 rounded-2xl border ${plan.is_popular ? 'border-purple-500' : 'border-white/10'} hover:border-purple-500/50 transition-all duration-300 flex flex-col justify-between w-full`}>
                                        <h3 className="text-2xl font-bold text-white mb-2">{formatPlanName(plan.name)}</h3>
                                        <p className="text-white/60 mb-6">{plan.description}</p>

                                        <div className="mb-6">
                                            <span className="text-5xl font-bold text-white">
                                                {plan.price === 0 ? 'Free' : `₹${plan.price.toLocaleString('en-IN')}`}
                                            </span>
                                            {plan.duration_days && (
                                                <span className="text-white/60 ml-2">/ {plan.duration_days} days</span>
                                            )}
                                        </div>

                                        <div className="flex-grow">
                                            <ul className="space-y-3 mb-6">
                                                {plan.features.map((feature, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-white/80">
                                                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                                                        <span className="text-sm leading-relaxed">{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <button
                                            onClick={onGetStarted}
                                            className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 ${plan.is_popular
                                                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg hover:shadow-purple-500/50'
                                                : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                                                }`}
                                        >
                                            Get Started
                                        </button>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    )
}

// CTA Section
const CTASection = ({ onGetStarted }) => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: true })

    return (
        <section ref={sectionRef} className="relative py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
            {/* Animated background */}
            <div className="absolute inset-0">
                <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20"
                    animate={{
                        backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                    }}
                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                />
            </div>

            <motion.div
                className="max-w-4xl mx-auto text-center relative z-10"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
            >
                <h2 className="text-4xl sm:text-6xl font-bold text-white mb-6">
                    Ready to Transform Your Customer Experience?
                </h2>
                <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
                    Join thousands of businesses using Talkida to provide instant, intelligent customer support 24/7
                </p>

                <motion.button
                    onClick={onGetStarted}
                    className="group px-10 py-5 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-full shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 text-lg flex items-center gap-3 mx-auto"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    Get Started Free - No Credit Card Required
                    <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
                </motion.button>
            </motion.div>
        </section>
    )
}

// Our Clients Section
const OurClientsSection = () => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: true, amount: 0.3 })
    const [showAllClients, setShowAllClients] = useState(false)

    // Featured clients (prominently displayed)
    const featuredClients = [
        {
            name: 'Eaze Alliance',
            website: 'eazealliance.in',
            url: 'https://eazealliance.in',
            description: 'Digital transformation solutions',
            logo: null,
            category: 'Enterprise'
        },
        {
            name: 'Primosa',
            website: 'primosa.in', 
            url: 'https://primosa.in',
            description: 'Business consulting services',
            logo: null,
            category: 'Consulting'
        },
        {
            name: 'Veltrionex',
            website: 'veltrionex.in',
            url: 'https://veltrionex.in', 
            description: 'Technology solutions provider',
            logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjMDAwMDAwIi8+CjxwYXRoIGQ9Ik0yMCAyMEw0MCA2MEw2MCAyMEg4MEw1MCA4MEwyMCA4MEw1MCAyMEgyMFoiIGZpbGw9IiMzNTcwQkYiLz4KPC9zdmc+',
            category: 'Technology'
        }
    ]

    // Only use real clients - no dummy data
    const allClients = featuredClients
    
    // Only show carousel if we have more than 6 clients
    const shouldShowCarousel = allClients.length > 6

    return (
        <section id="clients" ref={sectionRef} className="relative py-24 px-4 sm:px-6 lg:px-8">
            {/* Background with subtle pattern */}
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-transparent"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(139,92,246,0.1)_0%,transparent_50%)]"></div>
            
            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header Section */}
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                >
                    {/* Badge */}
                    <motion.div
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-md rounded-full border border-white/10 mb-6"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={isInView ? { opacity: 1, scale: 1 } : {}}
                        transition={{ delay: 0.1 }}
                    >
                        <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-400 to-pink-400"></div>
                        <span className="text-sm font-medium text-white/80">Trusted Partners</span>
                    </motion.div>

                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                        Powering Innovation for
                        <br />
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400">
                            Industry Leaders
                        </span>
                    </h2>
                    <p className="text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
                        Join forward-thinking companies who trust Talkida AI to transform their customer engagement and drive business growth
                    </p>
                </motion.div>

                {/* Only show carousel if we have many clients */}
                {shouldShowCarousel && (
                    <>
                        {/* Stats Bar */}
                        <motion.div
                            className="flex flex-wrap justify-center items-center gap-8 mb-16 text-center"
                            initial={{ opacity: 0 }}
                            animate={isInView ? { opacity: 1 } : {}}
                            transition={{ delay: 0.3 }}
                        >
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green-400 to-emerald-500"></div>
                                <span className="text-white/70 text-sm font-medium">{allClients.length}+ Active Clients</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-400 to-cyan-500"></div>
                                <span className="text-white/70 text-sm font-medium">Multiple Industries</span>
                            </div>
                        </motion.div>

                        {/* Infinite Scrolling Logo Carousel */}
                        <motion.div
                            className="mb-16 overflow-hidden"
                            initial={{ opacity: 0 }}
                            animate={isInView ? { opacity: 1 } : {}}
                            transition={{ delay: 0.4 }}
                        >
                            <motion.div
                                className="flex gap-12 py-8"
                                animate={{ x: [-600, 0] }}
                                transition={{
                                    duration: 20,
                                    repeat: Infinity,
                                    ease: "linear"
                                }}
                            >
                                {[...allClients, ...allClients].map((client, index) => (
                                    <div key={index} className="flex-shrink-0 flex items-center justify-center w-24 h-16 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                                        {client.logo ? (
                                            <img 
                                                src={client.logo}
                                                alt={`${client.name} logo`}
                                                className="w-12 h-12 object-contain opacity-70 hover:opacity-100 transition-opacity"
                                            />
                                        ) : (
                                            <div className="text-white/60 text-xs font-medium text-center px-2">
                                                {client.name}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </motion.div>
                        </motion.div>
                    </>
                )}

                {/* Our Clients */}
                <motion.div
                    className="mb-12"
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : {}}
                    transition={{ delay: shouldShowCarousel ? 0.5 : 0.3 }}
                >
                    <h3 className="text-2xl font-bold text-white text-center mb-8">
                        {shouldShowCarousel ? 'Featured Partners' : 'Our Trusted Partners'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                        {allClients.map((client, index) => {
                            // Calculate animation direction from center outward
                            const centerIndex = Math.floor(allClients.length / 2)
                            const isCenter = index === centerIndex
                            const isLeft = index < centerIndex
                            const distance = Math.abs(index - centerIndex)
                            
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ 
                                        opacity: 0, 
                                        x: isCenter ? 0 : isLeft ? -50 : 50,
                                        scale: 0.8
                                    }}
                                    animate={isInView ? { 
                                        opacity: 1, 
                                        x: 0,
                                        scale: 1
                                    } : {}}
                                    transition={{ 
                                        delay: (shouldShowCarousel ? 0.6 : 0.4) + (distance * 0.15), 
                                        duration: 0.6,
                                        ease: "easeOut"
                                    }}
                                    className="group"
                                >
                                    <a
                                        href={client.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block relative p-6 rounded-2xl bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] hover:border-white/20 transition-all duration-500 hover:bg-white/[0.05] group-hover:scale-[1.02] transform-gpu"
                                    >
                                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                    
                                    <div className="relative z-10 text-center">
                                        <div className="relative mb-4 mx-auto w-fit">
                                            <div className="w-14 h-14 rounded-xl overflow-hidden bg-white shadow-lg group-hover:shadow-xl group-hover:shadow-purple-500/20 transition-all duration-500 flex items-center justify-center">
                                                {client.logo ? (
                                                    <img 
                                                        src={client.logo}
                                                        alt={`${client.name} logo`}
                                                        className="w-full h-full object-cover rounded-xl"
                                                    />
                                                ) : (
                                                    <img 
                                                        src={`https://www.google.com/s2/favicons?domain=${client.website}&sz=128`}
                                                        alt={`${client.name} logo`}
                                                        className="w-8 h-8 object-contain"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <h4 className="text-lg font-semibold text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-300 group-hover:to-pink-300 transition-all duration-300">
                                                {client.name}
                                            </h4>
                                            <div className="flex items-center justify-center gap-2">
                                                <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full">
                                                    {client.category}
                                                </span>
                                            </div>
                                            <p className="text-white/50 text-sm">
                                                {client.description}
                                            </p>
                                        </div>
                                    </div>
                                </a>
                            </motion.div>
                        )
                        })}
                    </div>
                </motion.div>

                {/* Always show "See More" button for future scalability */}
                <motion.div
                    className="text-center mt-8"
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : {}}
                    transition={{ delay: 0.8 }}
                >
                    <button
                        onClick={() => setShowAllClients(!showAllClients)}
                        className="group inline-flex items-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 backdrop-blur-md rounded-full border border-white/10 hover:border-purple-500/50 transition-all duration-300 text-white font-medium"
                    >
                        <span>{showAllClients ? 'Show Less' : 'See More'}</span>
                        <motion.div
                            animate={{ rotate: showAllClients ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <ChevronDown className="w-4 h-4" />
                        </motion.div>
                    </button>
                </motion.div>

                {/* Expandable Section - Ready for additional clients */}
                <AnimatePresence>
                    {showAllClients && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.5 }}
                            className="mt-12 overflow-hidden"
                        >
                            {/* Grid container ready for future clients */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                                {/* Additional clients will appear here when added */}
                                {allClients.slice(3).map((client, index) => (
                                    <motion.div
                                        key={index + 3}
                                        initial={{ opacity: 0, y: 40 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1, duration: 0.6 }}
                                        className="group"
                                    >
                                        <a
                                            href={client.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block relative p-6 rounded-2xl bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] hover:border-white/20 transition-all duration-500 hover:bg-white/[0.05] group-hover:scale-[1.02] transform-gpu"
                                        >
                                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                            
                                            <div className="relative z-10 text-center">
                                                <div className="relative mb-4 mx-auto w-fit">
                                                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-white shadow-lg group-hover:shadow-xl group-hover:shadow-purple-500/20 transition-all duration-500 flex items-center justify-center">
                                                        {client.logo ? (
                                                            <img 
                                                                src={client.logo}
                                                                alt={`${client.name} logo`}
                                                                className="w-full h-full object-cover rounded-xl"
                                                            />
                                                        ) : (
                                                            <img 
                                                                src={`https://www.google.com/s2/favicons?domain=${client.website}&sz=128`}
                                                                alt={`${client.name} logo`}
                                                                className="w-8 h-8 object-contain"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling.style.display = 'flex';
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="space-y-2">
                                                    <h4 className="text-lg font-semibold text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-300 group-hover:to-pink-300 transition-all duration-300">
                                                        {client.name}
                                                    </h4>
                                                    <div className="flex items-center justify-center gap-2">
                                                        <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full">
                                                            {client.category}
                                                        </span>
                                                    </div>
                                                    <p className="text-white/50 text-sm">
                                                        {client.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </a>
                                    </motion.div>
                                ))}
                            </div>
                            
                            {/* Show message when no additional clients */}
                            {allClients.length <= 3 && (
                                <div className="text-center py-8">
                                    <div className="text-white/40 text-sm">
                                        More partners coming soon...
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    )
}

// Floating 3D Elements
const FloatingElements = () => {
    const elements = [
        { size: 100, left: '10%', top: '20%', delay: 0 },
        { size: 150, right: '10%', top: '40%', delay: 2 },
        { size: 80, left: '15%', bottom: '30%', delay: 1 },
        { size: 120, right: '15%', bottom: '20%', delay: 3 }
    ]

    return (
        <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
            {elements.map((el, i) => (
                <motion.div
                    key={i}
                    className="absolute rounded-full bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-sm"
                    style={{
                        width: el.size,
                        height: el.size,
                        left: el.left,
                        right: el.right,
                        top: el.top,
                        bottom: el.bottom
                    }}
                    animate={{
                        y: [0, -30, 0],
                        rotate: [0, 180, 360],
                        scale: [1, 1.1, 1],
                    }}
                    transition={{
                        duration: 15 + el.delay,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: el.delay
                    }}
                />
            ))}
        </div>
    )
}

export default LandingPage